import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';
import GaleriaHero from './components/GaleriaHero';
import FiltrosCategorias from './components/FiltrosCategorias';
import CartaCard from './components/CartaCard';
import CTAGaleria from './components/CTAGaleria';
import { cartasInspiradorasMock } from '../../mocks/galeria';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const galeriaSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Galeria de Cartas | Inspiração — Carta do Eu Futuro',
  url: `${SITE_URL}/galeria`,
  description: 'Explore a galeria de cartas inspiradoras do Carta do Eu Futuro. Descubra histórias reais de transformação pessoal através da escrita reflexiva e do poder da visualização.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Galeria', item: `${SITE_URL}/galeria` },
    ],
  },
};

export default function GaleriaPage() {
  const navigate = useNavigate();
  const [categoriaAtiva, setCategoriaAtiva] = useState('todas');
  const [cartasFiltradas, setCartasFiltradas] = useState(cartasInspiradorasMock);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (categoriaAtiva === 'todas') {
      setCartasFiltradas(cartasInspiradorasMock);
    } else {
      setCartasFiltradas(
        cartasInspiradorasMock.filter(carta => carta.categoria === categoriaAtiva)
      );
    }
  }, [categoriaAtiva]);

  return (
    <div className="min-h-screen bg-black">
      <SEOHead
        title="Galeria de Cartas Inspiradoras | Transformação Pessoal — Carta do Eu Futuro"
        description="Explore a galeria de cartas inspiradoras do Carta do Eu Futuro. Descubra histórias reais de transformação pessoal através da escrita reflexiva e do poder da visualização."
        keywords="galeria cartas, cartas inspiradoras, transformação pessoal, escrita reflexiva, desenvolvimento pessoal"
        canonical="/galeria"
        ogImage="https://public.readdy.ai/ai/img_res/5064ce2d9ccd8ca50ce057ae98c45ed1.jpg"
        ogType="website"
        schema={galeriaSchema}
      />
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-lg shadow-lg shadow-black/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="font-playfair text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300 hover:scale-105 transition-transform duration-300"
          >
            Carta do Eu Futuro
          </button>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-300 hover:text-amber-400 transition-colors duration-300 whitespace-nowrap"
            >
              Início
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300 whitespace-nowrap"
            >
              Minhas Cartas
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <GaleriaHero />

      {/* Filtros */}
      <FiltrosCategorias 
        categoriaAtiva={categoriaAtiva}
        onCategoriaChange={setCategoriaAtiva}
      />

      {/* Grid de Cartas */}
      <section className="bg-gray-900 py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Contador de resultados */}
          <div className="mb-8 flex items-center justify-between">
            <div className="text-gray-400">
              Mostrando <span className="text-amber-400 font-semibold">{cartasFiltradas.length}</span> {cartasFiltradas.length === 1 ? 'carta' : 'cartas'}
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <i className="ri-sort-desc text-amber-400"></i>
              <span>Ordenado por popularidade</span>
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cartasFiltradas.map((carta) => (
              <CartaCard
                key={carta.id}
                autor={carta.autor}
                cidade={carta.cidade}
                data={carta.data}
                trecho={carta.trecho}
                likes={carta.likes}
                tags={carta.tags}
              />
            ))}
          </div>

          {/* Mensagem quando não há resultados */}
          {cartasFiltradas.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center bg-white/5 rounded-full mx-auto mb-6">
                <i className="ri-inbox-line text-gray-400 text-4xl"></i>
              </div>
              <h3 className="text-white text-2xl font-semibold mb-3">Nenhuma carta encontrada</h3>
              <p className="text-gray-400 mb-6">Tente selecionar outra categoria</p>
              <button
                onClick={() => setCategoriaAtiva('todas')}
                className="px-6 py-3 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-full hover:bg-amber-500/20 transition-all duration-300 whitespace-nowrap"
              >
                Ver todas as cartas
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Final */}
      <CTAGaleria />

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">
            © 2024 Carta do Eu Futuro. Transformando vidas através da escrita.
          </p>
        </div>
      </footer>
    </div>
  );
}